import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { 
  CheckCircle, Loader2, ArrowRight, ArrowLeft, 
  Wrench, Droplets, MessageSquare, PoundSterling, 
  ShieldAlert, Scale, Users, Home, Building2,
  Rocket, Eye, SkipForward, Shield, Database,
  ClipboardCheck, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { siteStage } from '../../config/siteStage';
import ConfidentialityUnlock from './ConfidentialityUnlock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAIN_POINTS = [
  {
    id: 'repairs_maintenance',
    label: 'Repairs & Maintenance',
    description: 'Reporting issues, chasing contractors, failed appointments',
    icon: Wrench
  },
  {
    id: 'damp_mould',
    label: 'Damp & Mould',
    description: 'Persistent damp, mould growth, health concerns',
    icon: Droplets
  },
  {
    id: 'communication_records',
    label: 'Communication & Record Keeping',
    description: 'Logging calls, keeping evidence, proving what was said or promised',
    icon: MessageSquare
  },
  {
    id: 'bills_costs_charges',
    label: 'Bills, Costs & Service Charges',
    description: 'Understanding charges, bill disputes, fuel poverty, service charge transparency',
    icon: PoundSterling
  },
  {
    id: 'safety_concerns',
    label: 'Safety Concerns',
    description: 'Fire safety, electrical hazards, gas safety, building safety',
    icon: ShieldAlert
  },
  {
    id: 'tenant_rights',
    label: 'Tenant Rights & Escalation',
    description: 'Knowing your rights, making complaints, escalating to Ombudsman',
    icon: Scale
  },
  {
    id: 'antisocial_behaviour',
    label: 'Anti-Social Behaviour',
    description: 'Noise, neighbour disputes, community safety',
    icon: Users
  }
];

const PROVIDER_PAIN_POINTS = [
  {
    id: 'provider_contractor_backlogs',
    label: 'Chasing Contractors & Repair Backlogs',
    description: 'Missed appointments, jobs marked complete that aren\'t, growing backlogs',
    icon: Wrench
  },
  {
    id: 'provider_damp_mould_casework',
    label: 'Damp & Mould Case Management',
    description: 'Tracking cases end-to-end, Awaab\'s Law timescales, evidencing action taken',
    icon: Droplets
  },
  {
    id: 'provider_fragmented_records',
    label: 'Fragmented Records Across Systems',
    description: 'Issue history split across inboxes, spreadsheets and legacy systems',
    icon: Database
  },
  {
    id: 'provider_resident_communication',
    label: 'Resident Communication & Complaint Volume',
    description: 'Repeat contact, keeping residents updated, complaints that could\'ve been avoided',
    icon: MessageSquare
  },
  {
    id: 'provider_compliance_reporting',
    label: 'Compliance & Regulatory Reporting Burden',
    description: 'Evidencing standards, inspection readiness, keeping up with new regulation',
    icon: ClipboardCheck
  },
  {
    id: 'provider_ombudsman_evidence',
    label: 'Evidence for Ombudsman & Disputes',
    description: 'Reconstructing what happened and when, once a case escalates',
    icon: Scale
  },
  {
    id: 'provider_staff_admin_time',
    label: 'Staff Time Lost to Admin & Repeat Contact',
    description: 'Manual logging, duplicated data entry, answering the same question twice',
    icon: Clock
  }
];

const PROVIDER_EXAMPLE_STORIES = [
  "\"We only found out a resident had reported the same issue four times when it reached the Ombudsman — the history lived in three different systems.\"",
  "\"Half my week goes on chasing contractors and updating residents who've heard nothing. The work gets done — proving it is the hard part.\"",
  "\"Every regulation change means another spreadsheet. We're a small team, and the reporting burden grows faster than we do.\""
];

const EXAMPLE_STORIES = [
  "\"I reported damp 6 times over 2 years. Each time I had to start from scratch because there was no record of my previous calls.\"",
  "\"My service charges went up 40% but nobody could explain what changed. I had no way to challenge it.\"",
  "\"I didn't know I could escalate to the Ombudsman until a neighbour told me. By then I'd been waiting 8 months for a repair.\""
];

const INTEREST_LABELS = {
  founder_access: 'Founder Access',
  free: 'Free plan',
  extra: 'Extra plan',
  premium: 'Premium plan',
  provider: 'Provider partnership',
  provider_pilot: 'Provider pilot',
  resident_evidence_pilot: 'Resident pilot',
  sponsorship: 'Landlord/provider sponsorship',
  survey: 'General interest',
};

const PainPointSurvey = ({ onSkip }) => {
  const navigate = useNavigate();
  const formRef = React.useRef(null);
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('resident');
  const [interestType, setInterestType] = useState('survey');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tenant_type: '',
    pain_points_selected: [],
    story: '',
    subscribe_blog: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pilotHandoff, setPilotHandoff] = useState(null);
  const [error, setError] = useState('');

  // Listen for plan CTA clicks that pre-fill interest_type
  React.useEffect(() => {
    const handler = (e) => {
      const it = e?.detail?.interestType;
      if (it && INTEREST_LABELS[it]) {
        setInterestType(it);
        // Highlight the form briefly so the user sees the badge
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 150);
      }
    };
    window.addEventListener('resiax:set-interest', handler);
    return () => window.removeEventListener('resiax:set-interest', handler);
  }, []);

  // Auto-scroll form into view only on successful submission
  React.useEffect(() => {
    if (isSuccess && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isSuccess]);

  const handlePainPointToggle = (id) => {
    setFormData(prev => ({
      ...prev,
      pain_points_selected: prev.pain_points_selected.includes(id)
        ? prev.pain_points_selected.filter(p => p !== id)
        : [...prev.pain_points_selected, id]
    }));
  };

  const submitToApi = async (payload) => {
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waiting-list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to submit');
        }
        return await response.json();
      } catch (err) {
        if (attempt < maxRetries && err.message === 'Failed to fetch') {
          await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        throw err;
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const result = await submitToApi({
        name: formData.name,
        email: formData.email,
        user_type: role === 'provider' ? 'housing_professional' : 'resident',
        tenant_type: role === 'provider' ? '' : formData.tenant_type,
        pain_points_selected: formData.pain_points_selected,
        pain_points: formData.story,
        desired_features: '',
        subscribe_blog: formData.subscribe_blog,
        interest_type: interestType,
        source_page: (typeof window !== 'undefined' ? window.location.pathname : '/'),
      });

      if (role === 'resident') {
        localStorage.setItem('resiax_access', 'granted');
      }
      setPilotHandoff(result?.handoff_token ? { registrationId: result.pilot_registration_id, token: result.handoff_token } : null);
      setIsSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipToWaitlist = async () => {
    if (!formData.name || !formData.email) {
      setStep(1);
      setError('Please provide your name and email to join the waitlist.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const result = await submitToApi({
        name: formData.name,
        email: formData.email,
        user_type: role === 'provider' ? 'housing_professional' : 'resident',
        tenant_type: '',
        pain_points_selected: [],
        pain_points: '',
        desired_features: '',
        subscribe_blog: formData.subscribe_blog,
        interest_type: interestType,
        source_page: (typeof window !== 'undefined' ? window.location.pathname : '/'),
      });

      if (role === 'resident') {
        localStorage.setItem('resiax_access', 'granted');
      }
      setPilotHandoff(result?.handoff_token ? { registrationId: result.pilot_registration_id, token: result.handoff_token } : null);
      setIsSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    const interestLabel = INTEREST_LABELS[interestType] || 'General interest';
    return (
      <div ref={formRef} data-testid="survey-success" className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-[#1F1E1E] mb-2">You&apos;re on the list!</h3>
          <p className="text-[#3B3535]/70 max-w-md mx-auto">
            Thanks for sharing. We&apos;ll be in touch soon with next steps and to answer any questions.
          </p>
        </div>

        {interestType !== 'survey' && (
          <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 text-[#B5541B] text-sm font-semibold px-4 py-2 rounded-full">
            <span className="w-2 h-2 rounded-full bg-[#B5541B]" />
            Interest noted: {interestLabel}
          </div>
        )}

        <p className="text-sm text-[#3B3535]/60 max-w-md mx-auto">
          {role === 'provider'
            ? 'Thanks for sharing the provider side of the story — it directly shapes what we build.'
            : 'Ready to get started? Head over to the ResiAX app to log in or create your account.'}
        </p>

        {interestType === 'resident_evidence_pilot' && !pilotHandoff?.token && (
          <p data-testid="pilot-handoff-notice" className="text-xs text-[#B5541B] bg-[#B5541B]/10 rounded-lg px-4 py-3 max-w-md mx-auto">
            Your pilot place is registered, but we couldn&apos;t prepare your automatic pilot
            verification just now. You can still continue to the app — our team will confirm
            your pilot access for you, or contact us if anything looks off.
          </p>
        )}

        {role === 'provider' ? (
          <ConfidentialityUnlock email={formData.email} name={formData.name} source="survey-provider" />
        ) : (
        <div className="flex flex-col items-center gap-3 pt-2">
          <Button
            data-testid="continue-to-app-btn"
            onClick={() => {
              if (siteStage.opaqueHandoffEnabled && pilotHandoff?.token) {
                const url = new URL(siteStage.pilotStartUrl);
                url.searchParams.set('token', pilotHandoff.token);
                window.location.href = url.toString();
                return;
              }
              window.location.href = siteStage.appUrl;
            }}
            className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            Continue to the ResiAX app
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <button
            data-testid="view-backlog-btn"
            onClick={() => navigate('/features')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors"
          >
            <Eye className="w-4 h-4" />
            Or see the feature backlog first
          </button>
        </div>
        )}
      </div>
    );
  }

  return (
    <div ref={formRef} className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl border border-gray-100">
      {/* Interest badge (shown when a plan CTA prefills this form) */}
      {interestType !== 'survey' && (
        <div
          data-testid="interest-badge"
          className="mb-5 inline-flex items-center gap-2 bg-[#B5541B]/10 text-[#B5541B] text-sm font-semibold px-4 py-2 rounded-full"
        >
          <span className="w-2 h-2 rounded-full bg-[#B5541B] animate-pulse" />
          Interested in: {INTEREST_LABELS[interestType]}
          <button
            type="button"
            onClick={() => setInterestType('survey')}
            className="ml-1 text-[#B5541B]/60 hover:text-[#B5541B]"
            aria-label="Clear interest"
          >
            ×
          </button>
        </div>
      )}

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map(s => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
              s <= step ? 'bg-[#B5541B]' : 'bg-gray-200'
            }`} />
          </div>
        ))}
        <span className="text-xs text-[#3B3535]/50 ml-2">{step}/3</span>
      </div>

      {/* Step 1: Basic info + Tenant type */}
      {step === 1 && (
        <div data-testid="survey-step-1" className="space-y-5">
          <div>
            <h3 className="text-xl font-bold text-[#1F1E1E] mb-1">Tell us about you</h3>
            <p className="text-sm text-[#3B3535]/70">Help us understand your situation so we can build the right solution.</p>
          </div>

          {/* Role choice */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="survey-role-resident"
              onClick={() => {
                if (role !== 'resident') setFormData(prev => ({ ...prev, pain_points_selected: [] }));
                setRole('resident');
              }}
              className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                role === 'resident' ? 'border-[#B5541B] bg-[#B5541B]/5 text-[#B5541B]' : 'border-gray-200 text-[#3B3535] hover:border-[#B5541B]/40'
              }`}
            >
              I&apos;m a resident / tenant
            </button>
            <button
              type="button"
              data-testid="survey-role-provider"
              onClick={() => {
                if (role !== 'provider') setFormData(prev => ({ ...prev, pain_points_selected: [] }));
                setRole('provider');
              }}
              className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                role === 'provider' ? 'border-[#B5541B] bg-[#B5541B]/5 text-[#B5541B]' : 'border-gray-200 text-[#3B3535] hover:border-[#B5541B]/40'
              }`}
            >
              I work in housing
            </button>
          </div>

          {/* Anonymity assurance */}
          <div className="bg-[#F0F9F0] rounded-lg px-4 py-2.5 flex items-start gap-2.5">
            <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-800 leading-relaxed">
              <strong>Your information is confidential.</strong>{' '}
              {role === 'provider'
                ? 'What you share stays with us and is only used to shape what we build.'
                : 'It will not be shared with your landlord without your permission.'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="survey-name" className="text-[#3B3535] font-medium">
              Name <span className="text-[#B5541B]">*</span>
            </Label>
            <Input
              id="survey-name"
              data-testid="survey-name-input"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="survey-email" className="text-[#3B3535] font-medium">
              Email <span className="text-[#B5541B]">*</span>
            </Label>
            <Input
              id="survey-email"
              data-testid="survey-email-input"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="you@example.com"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
            />
          </div>

          {role === 'resident' && (
          <div className="space-y-3">
            <Label className="text-[#3B3535] font-medium">
              Who is your landlord? <span className="text-[#B5541B]">*</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="tenant-type-social"
                onClick={() => setFormData({ ...formData, tenant_type: 'social' })}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                  formData.tenant_type === 'social'
                    ? 'border-[#B5541B] bg-[#B5541B]/5'
                    : 'border-gray-200 hover:border-[#B5541B]/40'
                }`}
              >
                <Building2 className={`w-6 h-6 ${formData.tenant_type === 'social' ? 'text-[#B5541B]' : 'text-gray-400'}`} />
                <div>
                  <p className="font-medium text-[#1F1E1E]">Social Landlord</p>
                  <p className="text-xs text-[#3B3535]/60">Housing association or council</p>
                </div>
              </button>
              <button
                type="button"
                data-testid="tenant-type-private"
                onClick={() => setFormData({ ...formData, tenant_type: 'private' })}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                  formData.tenant_type === 'private'
                    ? 'border-[#B5541B] bg-[#B5541B]/5'
                    : 'border-gray-200 hover:border-[#B5541B]/40'
                }`}
              >
                <Home className={`w-6 h-6 ${formData.tenant_type === 'private' ? 'text-[#B5541B]' : 'text-gray-400'}`} />
                <div>
                  <p className="font-medium text-[#1F1E1E]">Private Landlord</p>
                  <p className="text-xs text-[#3B3535]/60">Individual or lettings agency</p>
                </div>
              </button>
            </div>
          </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Subscribe to blog */}
          <div className="flex items-start gap-3 pt-2 pb-1 border-t border-gray-100 mt-2">
            <Checkbox
              id="survey-subscribe-blog"
              data-testid="survey-subscribe-blog"
              checked={formData.subscribe_blog}
              onCheckedChange={(v) => setFormData({ ...formData, subscribe_blog: !!v })}
              className="mt-0.5 data-[state=checked]:bg-[#B5541B] data-[state=checked]:border-[#B5541B]"
            />
            <Label htmlFor="survey-subscribe-blog" className="text-sm text-[#3B3535]/80 leading-relaxed cursor-pointer">
              Email me when new blog posts go live (occasional, no spam)
            </Label>
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              data-testid="skip-survey-btn"
              onClick={handleSkipToWaitlist}
              disabled={isSubmitting}
              className="text-sm text-[#3B3535]/60 hover:text-[#B5541B] transition-colors flex items-center gap-1"
            >
              <SkipForward className="w-3.5 h-3.5" />
              Skip, just add me to the list
            </button>
            <Button
              data-testid="survey-next-step-1"
              onClick={() => {
                if (!formData.name || !formData.email || (role === 'resident' && !formData.tenant_type)) {
                  setError('Please fill in all required fields.');
                  return;
                }
                setError('');
                setStep(2);
              }}
              className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-6 py-5 rounded-full shadow-md"
            >
              Next
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Pain point selection */}
      {step === 2 && (
        <div data-testid="survey-step-2" className="space-y-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-[#1F1E1E] mb-1">
              {role === 'provider' ? 'What slows your team down most?' : 'What frustrates you most?'}
            </h3>
            <p className="text-xs sm:text-sm text-[#3B3535]/70">Select all that apply. This helps us prioritise what to build first.</p>
          </div>

          <div className="space-y-1.5 sm:space-y-2.5">
            {(role === 'provider' ? PROVIDER_PAIN_POINTS : PAIN_POINTS).map(point => {
              const Icon = point.icon;
              const isSelected = formData.pain_points_selected.includes(point.id);
              return (
                <button
                  key={point.id}
                  type="button"
                  data-testid={`pain-point-${point.id}`}
                  onClick={() => handlePainPointToggle(point.id)}
                  className={`w-full flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3.5 rounded-xl border-2 transition-all duration-200 text-left ${
                    isSelected
                      ? 'border-[#B5541B] bg-[#B5541B]/5'
                      : 'border-gray-100 hover:border-[#B5541B]/30 bg-gray-50/50'
                  }`}
                >
                  <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-[#B5541B] text-white' : 'bg-white text-gray-400 border border-gray-200'
                  }`}>
                    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-xs sm:text-sm ${isSelected ? 'text-[#B5541B]' : 'text-[#1F1E1E]'}`}>
                      {point.label}
                    </p>
                    <p className="text-xs text-[#3B3535]/60 truncate hidden sm:block">{point.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-[#B5541B] bg-[#B5541B]' : 'border-gray-300'
                  }`}>
                    {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-3 sticky bottom-0 bg-white pb-1">
            <Button
              variant="ghost"
              onClick={() => setStep(1)}
              className="text-[#3B3535] hover:text-[#B5541B]"
            >
              <ArrowLeft className="mr-1 w-4 h-4" />
              Back
            </Button>
            <Button
              data-testid="survey-next-step-2"
              onClick={() => setStep(3)}
              className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-6 py-5 rounded-full shadow-md"
            >
              {formData.pain_points_selected.length > 0 ? 'Next' : 'Skip this'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Story sharing */}
      {step === 3 && (
        <div data-testid="survey-step-3" className="space-y-5">
          <div>
            <h3 className="text-xl font-bold text-[#1F1E1E] mb-1">Share your experience</h3>
            <p className="text-sm text-[#3B3535]/70">
              Optional — but powerful. What would you want to <strong>stop</strong>, <strong>start</strong>, or <strong>continue</strong> about {role === 'provider' ? 'managing homes' : 'being a tenant'}?
            </p>
          </div>

          {/* Example quotes */}
          <div className="bg-[#FFF8F5] rounded-xl p-4 space-y-2.5">
            <p className="text-xs font-semibold text-[#B5541B] uppercase tracking-wide">
              {role === 'provider' ? 'What other providers have shared:' : 'What other residents have shared:'}
            </p>
            {(role === 'provider' ? PROVIDER_EXAMPLE_STORIES : EXAMPLE_STORIES).map((quote, i) => (
              <p key={i} className="text-sm text-[#3B3535]/80 italic leading-relaxed">
                {quote}
              </p>
            ))}
          </div>

          <div className="space-y-2">
            <Label htmlFor="story" className="text-[#3B3535] font-medium">
              Your experience <span className="text-[#3B3535]/50">(optional)</span>
            </Label>
            <Textarea
              id="story"
              data-testid="survey-story-input"
              value={formData.story}
              onChange={(e) => setFormData({ ...formData, story: e.target.value })}
              placeholder={role === 'provider'
                ? "What's been your biggest frustration managing homes? What would you change if you could?"
                : "What's been your biggest frustration as a tenant? What would you change if you could?"}
              rows={4}
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => setStep(2)}
              className="text-[#3B3535] hover:text-[#B5541B]"
            >
              <ArrowLeft className="mr-1 w-4 h-4" />
              Back
            </Button>
            <Button
              data-testid="survey-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-8 py-5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PainPointSurvey;
